/**
 * @file buildHistory.ts
 * @description Shared conversation history builder for the credentialing chatbot.
 *              Used by both the public CredentialingChatWidget and the internal assistant.
 * @author William Donaldson
 * @last_modified 2026-04-16
 */

/** Max history messages to send as conversation context */
const MAX_HISTORY_MESSAGES = 4;

/** Max characters per history message sent to the API */
const MAX_HISTORY_CONTENT_CHARS = 500;

/** WHY PREFIX MATCH: The CTA wording may change but always starts with
 *  "\n\n---\n*[EnrollPilot]". Matching the prefix (indexOf) rather than
 *  exact text survives wording changes without widget updates. */
const CTA_MARKER = "\n\n---\n*[EnrollPilot]";

/** Sources that indicate a complete, usable response worth including in
 *  conversation history. Excludes FALLBACK (error recovery), PARTIAL
 *  (truncated stream), and undefined (rate-limit / error messages). */
const VALID_SOURCES = new Set([
  "RAG",
  "CACHE_EXACT",
  "CACHE_SEMANTIC",
  "GENERIC_TERM",
  "DISAMBIGUATION",
  "GENERAL",  // Tier 2 general knowledge answers from internal assistant
  "LLM_GENERAL", // Tier 2 general knowledge answers from public chatbot
  "PRODUCT_RAG", // EnrollPilot how-to answers from product user guides
  "PRODUCT_NO_DOCS", // Product feature named, detail not in its guide
  "PRODUCT_UNCOVERED", // EnrollPilot named, no guide for that feature yet
]);

interface HistoryMessage {
  role: "user" | "assistant";
  content: string;
  source?: string;
}

/**
 * Build conversation history from prior message pairs for multi-turn context.
 *
 * WHY PAIRS: The LLM needs matched user->assistant exchanges to understand
 * the conversation flow. Orphan messages (e.g., error responses without a
 * valid source) would confuse the model.
 */
export function buildHistory(
  messages: HistoryMessage[]
): Array<{ role: "user" | "assistant"; content: string }> {
  const result: Array<{ role: "user" | "assistant"; content: string }> = [];

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const next = messages[i + 1];

    if (
      msg &&
      next &&
      msg.role === "user" &&
      next.role === "assistant" &&
      msg.content &&
      next.content &&
      // WHY STRIP _FOLLOWUP: The route suffixes follow-up sources (e.g.,
      // "RAG_FOLLOWUP"). The base source is still valid for history.
      VALID_SOURCES.has((next.source ?? "").replace(/_FOLLOWUP$/, ""))
    ) {
      let assistantContent = next.content;
      const ctaIdx = assistantContent.indexOf(CTA_MARKER);
      if (ctaIdx !== -1) {
        assistantContent = assistantContent.slice(0, ctaIdx);
      }

      result.push(
        { role: "user", content: clampHistoryContent(msg.content) },
        { role: "assistant", content: clampHistoryContent(assistantContent) }
      );
      i++; // Skip the assistant message -- already processed as part of pair
    }
  }

  return result.slice(-MAX_HISTORY_MESSAGES);
}

function clampHistoryContent(content: string): string {
  return content.length > MAX_HISTORY_CONTENT_CHARS
    ? content.slice(0, MAX_HISTORY_CONTENT_CHARS)
    : content;
}
